/* CURRENCY KEEP — ACT 3: WHERE A PHOTOGRAPH IS READ FROM.
 *
 * PHASE 4, STEP 7. Until now every screen read the LOCAL preview and
 * nothing else. Act 2 put four photographs in R2 and the app could not
 * display one of them — only `contactSheet()` could. This module is the
 * seam that closes that.
 *
 * ══ ⚠⚠⚠ WHAT IT DOES NOT DO ═══════════════════════════════════════
 * IT DELETES NOTHING. Jesse, 2026-08-25: *"don't delete the local copies
 * as part of Act 3 — the previews stay until I say otherwise."* There is
 * no delete in this file and Act 4 remains a separate pass.
 *
 * ══ ⚠⚠ FIVE STATES, AND COLLAPSING ANY TWO IS THE BUG THIS PROJECT
 *       KEEPS FINDING ════════════════════════════════════════════════
 *
 *   empty        no photograph is claimed. The ordinary case.
 *   local        bytes are in THIS browser. Renders with no network.
 *   remote       an `r2:` ref — in the bucket, needs fetching.
 *   missing      claimed, and not on this device, and not in the bucket.
 *                An `idb:` ref with no local bytes. PERMANENT until the
 *                collector re-photographs it.
 *   broken       the ref cannot be read at all.
 *
 * ⚠⚠ AND `unavailable` IS A SIXTH THING THAT ONLY `resolveSlot` CAN
 * PRODUCE: an `r2:` ref whose FETCH FAILED. **It must never render as
 * `missing`.** "Not on this device" is permanent and tells the collector
 * to re-take the photograph; "could not load" is transient and tells them
 * to retry. Collapsing them would tell someone on a train that their
 * photograph is gone. That is `shared/photo-ref.js`'s null-vs-malformed
 * rule, one layer up, at the point where a person reads the answer.
 *
 * ══ ⚠ LOCAL FIRST, AND IT IS A REQUIREMENT RATHER THAN AN OPTIMISATION
 * Jesse's standing quality bar, recorded against the archive cap:
 * *"'Top quality at all times, ready instantly' was BOTH halves."* A
 * photograph already in this browser renders with no network at all, so
 * while the previews exist there is NO regression on any screen.
 *
 * ⚠⚠ WHICH IS ALSO HOW THIS MODULE COULD FLATTER ITSELF. With a local
 * copy present the R2 path never runs, and Act 3 would look green while
 * proving nothing — the stand-in shape this phase has logged repeatedly.
 * So `preferRemote` forces the bucket path with the local copy still in
 * place, the gate asserts BOTH orders, and `__ckAct3.preferRemote(true)`
 * makes it switchable by hand on a real vault.
 *
 * ⚠ AFTER ACT 4 NOTHING HERE CHANGES. Retiring the previews removes the
 * `local` branch's INPUT, not the branch. The same code then resolves
 * every migrated slot through the bucket, which is why the swap and the
 * retirement are separable at all.
 */

import { parsePhotoRef } from '../shared/photo-ref.js'

/** The tier each surface asks for. `src/` had no tier concept before
 *  this; these are the names the delivery route already serves.
 *
 *  ⚠ NOT A DEVICE-CLASS DECISION. BUILD_NOTES item 4 measured that
 *  `devicePixelRatio` decides which tier a screen needs and device CLASS
 *  does not — Jesse's own phone in landscape demands more device pixels
 *  than a 1440p desktop. That choice belongs to the surface that knows
 *  its own CSS box, so it is a parameter here rather than a constant. */
export const TIER_FOR = Object.freeze({
  chip: 'thumb',
  detail: 'display',
  zoom: 'archive',
  community: 'thumb',
})

export const STATE = Object.freeze({
  EMPTY: 'empty',
  LOCAL: 'local',
  REMOTE: 'remote',
  MISSING: 'missing',
  BROKEN: 'broken',
  UNAVAILABLE: 'unavailable',
})

/**
 * What a slot IS, with no network and no side effects.
 *
 * `hasLocal` is a boolean the caller has already established by reading
 * the local store — it is not re-derived here, because a function that
 * reads storage cannot be a pure classifier and this one has to be
 * testable without one.
 *
 * ⚠ `r2:` WINS OVER A LOCAL COPY IN THE CLASSIFICATION, and the renderer
 * decides which to actually show. The ref is what the row asserts; the
 * local copy is a cache of it. Reversing that would mean a slot whose
 * bytes are in the bucket reports itself as local and stops being
 * fetchable the day the preview is retired.
 */
export function slotState(holding, slot, hasLocal) {
  const has = slot === 'front' ? holding?.hasFront : holding?.hasBack
  const ref = slot === 'front' ? holding?.frontRef : holding?.backRef
  const parsed = parsePhotoRef(ref)

  if (parsed.scheme === 'r2') return STATE.REMOTE
  if (parsed.scheme === 'malformed') return STATE.BROKEN
  if (parsed.scheme === 'idb') return hasLocal ? STATE.LOCAL : STATE.MISSING

  /* scheme null. A row that claims a photograph with no ref is the
   * in-memory drift `putPhoto` used to create; it is still reported
   * rather than silently rendered as an empty slot. */
  if (has) return hasLocal ? STATE.LOCAL : STATE.MISSING
  return STATE.EMPTY
}

/**
 * WHAT PROVES A SLOT'S PIXELS CAME OUT OF THE BUCKET.
 *
 * `served` and `local` are `{w, h}` or `null` — decoded dimensions, not
 * anything either module says about itself.
 *
 * ══ ⚠⚠⚠ THREE SITUATIONS, AND COLLAPSING TWO OF THEM WAS A BUG ═════
 *
 *   'dimensions'     a local preview EXISTS and the served image decodes
 *                    to a DIFFERENT size. The strong proof while both
 *                    copies are present: a preview is up to 3200 px
 *                    (`editor.js`), display is capped at 2000 and thumb
 *                    at 400, so the NUMBER names which file this is and
 *                    no bug in `resolveSlot` can fake it.
 *
 *   'no-local-copy'  there is NO local preview — the state after Act 4.
 *                    ⚠ THIS IS THE STRONGER PROOF, NOT THE WEAKER ONE.
 *                    A picture decoded and there is no local file it
 *                    could have come from, so the bytes crossed the
 *                    network. The dimension comparison only ever existed
 *                    to rule OUT a local copy that was also present.
 *
 *   null             a local preview exists and the served image is the
 *                    SAME size. Genuinely unproven, and the only case
 *                    that deserves a warning.
 *
 * ⚠ THE REASON THIS IS A FUNCTION IN A GATED MODULE RATHER THAN A
 * TERNARY IN A CONSOLE HELPER: the first version lived inline in
 * `main.js` and warned whenever the dimensions failed to differ — which
 * after Act 4 is EVERY migrated slot, because there is no preview left
 * to differ from. It would have told Jesse his photographs might not be
 * coming from R2, thirty seconds after an irreversible delete, and it
 * would have been wrong. A claim about provenance is exactly the kind of
 * thing this project requires to be checkable.
 */
export function provenanceOf({ served, local }) {
  if (!served) return null
  if (!local) return 'no-local-copy'
  return (served.w !== local.w || served.h !== local.h) ? 'dimensions' : null
}

/* ── the fetched-tier cache ────────────────────────────────────────── */

/* ⚠ WITHOUT THIS, EVERY RENDER REFETCHES EVERY PHOTOGRAPH. `hydratePhotos`
 * runs on each `render()`, and render is called on almost every
 * interaction — a filter, a keystroke in the search box. An uncached
 * fetch here would put a network request per photograph behind every
 * character typed.
 *
 * ⚠ IT CACHES THE BLOB URL, NOT THE BYTES, and never revokes while a
 * page is alive: an `<img>` still pointing at a revoked blob URL renders
 * broken, and there is no reliable moment to know the last one is gone.
 * The whole map is dropped on sign-out, which is the only point where
 * holding on would be wrong. */
const tierCache = new Map()
const cacheKey = (holdingId, slot, tier) => `${holdingId}:${slot}:${tier}`

/** Drop every cached tier. Called on sign-out — a different collector
 *  must not inherit blob URLs minted with the previous one's token. */
export function clearPhotoViewCache() {
  for (const url of tierCache.values()) {
    try { URL.revokeObjectURL(url) } catch (e) { /* already gone */ }
  }
  tierCache.clear()
}

/* ── resolving one slot to something renderable ────────────────────── */

/**
 * Turn a slot into `{ state, url }`.
 *
 * ⚠ THE ONLY FUNCTION HERE THAT TOUCHES THE NETWORK, and it reports a
 * failure as `unavailable` rather than throwing — a photograph that will
 * not load is a rendering state, not an exception, and a throw here would
 * take out the whole `hydratePhotos` loop and blank every OTHER
 * photograph on the screen.
 */
/* ⚠ THE SWITCH THAT MAKES THIS MODULE VERIFIABLE AT ALL. With a local
 * preview present the bucket path never runs, so "Act 3 works" would be
 * an untested claim on every screen. Setting this forces the fetch with
 * the previews still in place — the same move as running `step6-check`
 * with production in the preview slot: exercise the path you actually
 * want to trust, on real data, without destroying the fallback.
 *
 * ⚠ IT IS NOT PERSISTED. A reload returns to local-first, so nobody can
 * leave a collector's app in a slower mode by forgetting to unset it. */
let _preferRemote = false
export function setPreferRemote(on) { _preferRemote = !!on; return _preferRemote }
export function getPreferRemote() { return _preferRemote }

export async function resolveSlot({
  holding, slot, tier = TIER_FOR.detail,
  localUrl = null,
  fetchImpl = fetch, tokenImpl = null,
  preferRemote = null,
}) {
  if (preferRemote === null) preferRemote = _preferRemote
  const state = slotState(holding, slot, !!localUrl)

  if (state === STATE.EMPTY || state === STATE.MISSING || state === STATE.BROKEN) {
    return { state, url: null }
  }

  /* ⚠ LOCAL FIRST UNLESS TOLD OTHERWISE. Instant, no network, and no
   * regression while the previews exist. `preferRemote` is what stops
   * this module proving nothing — see the header. */
  if (state === STATE.LOCAL) return { state: STATE.LOCAL, url: localUrl }
  if (localUrl && !preferRemote) return { state: STATE.LOCAL, url: localUrl }

  const key = cacheKey(holding.id, slot, tier)
  const cached = tierCache.get(key)
  if (cached) return { state: STATE.REMOTE, url: cached, cached: true }

  try {
    const token = tokenImpl ? await tokenImpl() : null
    const res = await fetchImpl(`/api/photos/note/${holding.id}/${slot}/${tier}`, {
      headers: token ? { authorization: `Bearer ${token}` } : {},
    })
    if (!res.ok) {
      /* ⚠⚠ A 404 HERE IS *NOT* `missing`. The row says the bytes are in
       * the bucket; if the bucket disagrees that is a fault to surface,
       * not an instruction to the collector to re-photograph their note.
       * `missing` is reserved for a slot whose ref never left `idb:`. */
      return { state: STATE.UNAVAILABLE, url: localUrl, status: res.status }
    }
    const blob = await res.blob()
    const url = URL.createObjectURL(blob)
    tierCache.set(key, url)
    return { state: STATE.REMOTE, url, bytes: blob.size }
  } catch (err) {
    /* Offline, a dropped connection, a refused request. The local copy is
     * handed back when there is one, so a collector on a train keeps
     * seeing their photograph rather than an error over a picture the
     * browser is holding. */
    return { state: STATE.UNAVAILABLE, url: localUrl, error: String(err?.message ?? err) }
  }
}
